import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./styles.scss";
import { useQuery, useQueryClient } from "react-query";
import {
  fetchStripeCards,
  StripePaymentCard,
  createSetupIntent,
  SetupIntentResponse,
  deleteCard
} from "./api";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Elements } from "@stripe/react-stripe-js";
import imgMC from "../../../../components/CheckoutForm/img/mastercard.png";
import imgVisa from "../../../../components/CheckoutForm/img/visa.png";
import { setError, setLoading } from "../../../../redux/actions/app";
import { LANGUAGE, tt } from "../../../../utils/i18n/i18n";
import "./styles.scss";
import stripePromise from "../../../../components/stripePromise";
import { OVERRIDE_FONTS } from "../../../../components/CheckoutForm/utils/OVERRIDE_FONTS";
import { OVERRIDE_APPERANCE } from "../../../../components/CheckoutForm/utils/OVERRIDE_APPERANCE";
import { AddCardForm } from "./AddCardForm";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import TwemojiWrapper from "../../../../components/TwemojiWrapper/TwemojiWrapper";

const MY_ACCOUNT_CARDS_KEY = "my_account_cards";

const QUERY_OPTIONS = {
  staleTime: 30000,
  cacheTime: 300000,
  retry: 3,
  retryDelay: 1000,
  refetchOnWindowFocus: false
};

export default function () {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const queryClient = useQueryClient();
  const [showAddCard, setShowAddCard] = useState(false);
  const [setupIntent, setSetupIntent] = useState<SetupIntentResponse | null>(
    null
  );
  const [isCreatingSetupIntent, setIsCreatingSetupIntent] = useState(false);
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);

  const {
    data: cards,
    isLoading,
    isRefetching,
    isFetching
  } = useQuery(
    [MY_ACCOUNT_CARDS_KEY, accountToDisplay?.email],
    () => fetchStripeCards(accountToDisplay?.email || ""),
    {
      ...QUERY_OPTIONS,
      enabled: !!accountToDisplay?.email,
      onError: (error: any) => {
        dispatch(setError(error?.message || tt("api_error")));
      }
    }
  );

  const handleAddCard = async () => {
    if (!accountToDisplay?.email) return;

    dispatch(setLoading(true));
    setIsCreatingSetupIntent(true);
    try {
      const setupIntentResponse = await createSetupIntent(
        accountToDisplay.email
      );
      setSetupIntent(setupIntentResponse);
      setShowAddCard(true);
    } catch (error: any) {
      dispatch(setError(error?.message || tt("api_error")));
    } finally {
      setIsCreatingSetupIntent(false);
      dispatch(setLoading(false));
    }
  };

  const handleAddCardSuccess = () => {
    setShowAddCard(false);
    setSetupIntent(null);
    void queryClient.invalidateQueries([
      MY_ACCOUNT_CARDS_KEY,
      accountToDisplay?.email
    ]);
  };

  const handleAddCardCancel = () => {
    setShowAddCard(false);
    setSetupIntent(null);
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!accountToDisplay?.email) return;

    if (window.confirm(tt("confirm_delete_card"))) {
      setDeletingCardId(cardId);
      try {
        await deleteCard(cardId, accountToDisplay.email);
        void queryClient.invalidateQueries([
          MY_ACCOUNT_CARDS_KEY,
          accountToDisplay?.email
        ]);
      } catch (error: any) {
        dispatch(setError(error?.message || tt("api_error")));
      } finally {
        setDeletingCardId(null);
      }
    }
  };

  useEffect(() => {
    dispatch(setLoading(isLoading || isRefetching || isFetching));
  }, [isLoading, isRefetching, isFetching]);

  if (isLoading || isRefetching || isFetching) {
    return null;
  }

  return (
    <div className="d-flex flex-column st-gap">
      <div className="cards-container">
        {cards && cards.length > 0 ? (
          cards.map((card: StripePaymentCard, idx: number) => {
            const isVisa = card.cardType === "visa";
            const isMasterCard = card.cardType === "mastercard";
            return (
              <div
                key={`card-${idx}`}
                className="input-group d-flex flex-row align-items-center justify-content-center"
              >
                <div className="input-group-prepend">
                  <div className="input-group-text d-flex flex-row align-items-center justify-content-between">
                    <span className="d-block">{card.tokenizedCardNumber}</span>
                    <span className="d-block">
                      {tt("card_expires")} {`${card.expirationDate}`}
                    </span>
                    <div>
                      {isMasterCard && (
                        <img src={imgMC} style={{ height: 20 }} />
                      )}
                      {isVisa && <img src={imgVisa} style={{ height: 20 }} />}
                    </div>
                    {cards.length > 1 && (
                      <div
                        style={{
                          width: 50
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-link p-0 text-dark"
                          onClick={() => handleDeleteCard(card.id)}
                          disabled={deletingCardId === card.id}
                          title={tt("delete_card")}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className={
                              deletingCardId === card.id ? "fa-spin" : ""
                            }
                          />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="d-flex flex-column align-items-center st-gap">
            <TwemojiWrapper isBigText={true}>💳</TwemojiWrapper>
            <KC_UIText weight="reg" text={tt("no_saved_payment_methods")} />
          </div>
        )}
      </div>
      {showAddCard && setupIntent && (
        <div>
          <Elements
            stripe={stripePromise}
            options={{
              //@ts-ignore
              locale: LANGUAGE,
              //@ts-ignore
              appearance: OVERRIDE_APPERANCE,
              fonts: OVERRIDE_FONTS,
              // @ts-ignore
              clientSecret: setupIntent.clientSecret,
              loader: "always"
            }}
          >
            <AddCardForm
              businessUserId={accountToDisplay?.email || ""}
              setupIntentId={setupIntent.setupIntentId}
              onSuccess={handleAddCardSuccess}
              onCancel={handleAddCardCancel}
            />
          </Elements>
        </div>
      )}
      {!showAddCard && (
        <div className="w-100 d-flex flex-row justify-content-end">
          <KC_Button
            id="add-card"
            text={tt("add_card")}
            size={KC_ButtonSize.Large}
            disabled={isCreatingSetupIntent || showAddCard}
            onClick={handleAddCard}
            showIconOnTheLeft={true}
            icon={faPlus}
            className="add-card-button"
          />
        </div>
      )}
    </div>
  );
}
